import { AxiosResponse } from 'axios';
import { HttpClient } from 'api/http-client';
import { LinkedResource } from 'api/tasks';
import { IntegrationField, IntegrationProviderMeta } from '..';
import {
  JiraConfig,
  JiraEpic,
  JiraIssue,
  JiraissueLinkProps,
  JiraIssueType,
  JiraProject,
  ProjectValidationResponse,
  SettingsMeta,
} from './types';

const base = '/integrations/jira';

type CreateJiraIssue = {
  [key: string]: string | number;
};

class JiraHTTPClient extends HttpClient {
  public getJiraIntegrationProvider = (): Promise<IntegrationProviderMeta> =>
    this.get<IntegrationProviderMeta>('integration-providers/jira/meta');

  public getJiraIntegration = (): Promise<JiraConfig[]> =>
    this.get<JiraConfig[]>(`${base}`);

  public disconnectJiraAuthentication = (
    integrationId: number,
  ): Promise<void> => this.delete<void>(`${base}/${integrationId}`);

  public updateJiraIntegration = (
    integrationId: number,
    payload: SettingsMeta,
  ): Promise<SettingsMeta> =>
    this.put<SettingsMeta, SettingsMeta>(
      `${base}/${integrationId}/settings`,
      payload,
    );

  public createJiraIssue = (
    integrationId: number,
    payload: CreateJiraIssue,
  ): Promise<JiraIssue> =>
    this.post<JiraIssue, CreateJiraIssue>(
      `${base}/${integrationId}/api/issue/`,
      {
        ...payload,
      },
    );

  public getJiraIssue = (
    integrationId: number,
    issueId: string,
  ): Promise<JiraIssue> =>
    this.get<JiraIssue>(`${base}/${integrationId}/api/issue/${issueId}`);

  public getJiraIssueFields = (
    integrationId: number,
    projectId: string,
    issueTypeId: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/api/issue-fields?projectId=${projectId}&issueTypeId=${issueTypeId}`,
    );

  public getIssueTypeOptions = (
    integrationId: number,
    jiraProjectId: string,
  ): Promise<JiraIssueType[]> =>
    this.get<JiraIssueType[]>(
      `${base}/${integrationId}/api/issuetypes?projectId=${jiraProjectId}`,
    );

  public getJiraProjects = (integrationId: number): Promise<JiraProject[]> =>
    this.get<JiraProject[]>(`${base}/${integrationId}/api/projects`);

  public getJiraProjectIssueType = (
    integrationId: number,
    projectId: string,
  ): Promise<JiraIssueType[]> =>
    this.get<JiraIssueType[]>(
      `${base}/${integrationId}/api/issuetypes?projectId=${projectId}`,
    );

  public getJiraProjectEpics = (
    integrationId: number,
    projectId: string,
  ): Promise<JiraEpic[]> =>
    this.get<JiraEpic[]>(
      `${base}/${integrationId}/api/epics?projectId=${projectId}`,
    );

  public getJiraProjectRangeEpics = (
    integrationId: number,
    projectId: string,
  ): Promise<AxiosResponse<JiraEpic[]>> =>
    this.getResponse<JiraEpic[]>(
      `${base}/${integrationId}/api/epics?projectId=${projectId}`,
    );

  public getSearchJiraProjectEpics = (
    integrationId: number,
    projectId: string,
    queryString: string,
  ): Promise<JiraEpic[]> =>
    this.get<JiraEpic[]>(
      `${base}/${integrationId}/api/epics/search?projectId=${projectId}&query=${queryString}`,
    );

  public validJiraProjectChange = (
    integrationId: number,
    payload: SettingsMeta,
  ): Promise<{ errors: ProjectValidationResponse[] }> =>
    this.put<{ errors: ProjectValidationResponse[] }, SettingsMeta>(
      `${base}/${integrationId}/settings?validateTemplates=true`,
      payload,
    );

  public linkJiraIssueToTask = (
    integrationId: number,
    payload: JiraissueLinkProps,
  ): Promise<LinkedResource> =>
    this.post<LinkedResource, JiraissueLinkProps>(
      `${base}/${integrationId}/api/issue/resource-mapping`,
      payload,
    );

  public unlinkJiraIssueFromTask = (
    integrationId: number,
    resourceMappingId: number,
  ): Promise<number> =>
    this.delete<number>(
      `${base}/${integrationId}/resource-mapping/${resourceMappingId}`,
    );

  public deleteJiraIssue = (
    integrationId: number,
    jiraIssueId: string,
  ): Promise<number> =>
    this.delete(`${base}/${integrationId}/api/issue/${jiraIssueId}`);
}

export const JiraApi = new JiraHTTPClient();
